/**
 * #1091 (F1) — escopo do contrato especial como a tela do RS o monta e envia.
 *
 * A tela oferecia "Tipo de produto" e "Produto específico" SEM campo para escolher qual: o
 * alvo ia sempre nulo. Em produção isso derrubou a criação de VM do cliente (o backend lia um
 * atributo inexistente). Agora:
 * - o tipo-alvo sai de um conjunto FECHADO, o mesmo do backend (`TIPOS_DE_PRODUTO_DO_ESCOPO`
 *   em `special_contract_escopo.py`); no bloco só VM;
 * - sem alvo o formulário não envia (`faltaAlvo`);
 * - o payload leva os campos da modalidade certa, com o alvo que não se aplica como `null`
 *   EXPLÍCITO — é assim que trocar `product` → `product_type` num PUT limpa o produto antigo.
 */

export type ModalidadeDoContrato = 'resource_block' | 'simple_discount'
export type EscopoDoContrato = 'all' | 'product_type' | 'product'

type TipoDoProduto = {
  value: string
  label: string
}

/**
 * Tipos de produto admitidos no escopo, na ordem do backend.
 * Espelho de `TIPOS_DE_PRODUTO_DO_ESCOPO` em `backend/app/services/special_contract_escopo.py`.
 */
export const TIPOS_DE_PRODUTO_DO_ESCOPO: readonly TipoDoProduto[] = [
  { value: 'vm', label: 'Máquinas virtuais' },
  { value: 'block_storage', label: 'Block storage' },
  { value: 'network', label: 'IPv4 público' },
  { value: 'backup', label: 'Backup' },
]

/**
 * Tipos de produto admitidos pela modalidade.
 *
 * - Bloco (resource_block): só admite VM.
 * - Desconto (simple_discount): todos os tipos.
 */
export function tiposDoEscopo(modalidade: ModalidadeDoContrato): readonly TipoDoProduto[] {
  if (modalidade === 'resource_block') {
    return TIPOS_DE_PRODUTO_DO_ESCOPO.filter((t) => t.value === 'vm')
  }
  return TIPOS_DE_PRODUTO_DO_ESCOPO
}

/**
 * Os produtos do catálogo que o seletor de "Produto específico" pode oferecer.
 *
 * O catálogo da revenda traz tipos que o contrato não cobre (firewall, bundle, storage...); o
 * backend os recusa com 422 `produto_incompativel` (`validar_alvo`, #1091). Oferecer e depois
 * recusar seria o mesmo desacordo entre escrita e leitura que esta issue fecha — por isso o
 * filtro sai do MESMO conjunto de `tiposDoEscopo`.
 */
export function produtosDoEscopo<P extends { product_type: string }>(
  modalidade: ModalidadeDoContrato,
  produtos: readonly P[],
): P[] {
  const admitidos = new Set(tiposDoEscopo(modalidade).map((t) => t.value))
  return produtos.filter((p) => admitidos.has(p.product_type))
}

/**
 * Verifica se falta alvo para o escopo declarado.
 *
 * - `all`: não precisa de alvo.
 * - `product_type`: exige tipo.
 * - `product`: exige produtoId.
 * - `null` ou undefined: falta escopo.
 *
 * @param escopo O escopo declarado (ou nulo se não definido).
 * @param tipo O tipo de produto (vazio/nulo conta como ausente).
 * @param produtoId O id do produto (vazio/nulo conta como ausente).
 */
export function faltaAlvo(
  escopo: EscopoDoContrato | null | undefined,
  tipo: string | null | undefined,
  produtoId: string | null | undefined,
): boolean {
  if (!escopo) return true
  if (escopo === 'all') return false
  if (escopo === 'product_type') return !tipo
  if (escopo === 'product') return !produtoId
  return false
}

/**
 * Monta os campos de escopo e alvo a enviar pelo formulário.
 *
 * Usa o prefixo da modalidade (`block_` ou `discount_`), inclui o escopo,
 * e NULA os alvos que não se aplicam (assim um PUT troca de escopo limpa
 * o alvo anterior).
 *
 * @param modalidade A modalidade do contrato.
 * @param escopo O escopo escolhido.
 * @param tipo O tipo de produto (só entra se escopo é `product_type`).
 * @param produtoId O id do produto (só entra se escopo é `product`).
 */
export function camposDoEscopo(
  modalidade: ModalidadeDoContrato,
  escopo: EscopoDoContrato | null | undefined,
  tipo: string | null | undefined,
  produtoId: string | null | undefined,
): Record<string, string | null> {
  const prefix = modalidade === 'resource_block' ? 'block_' : 'discount_'
  return {
    [`${prefix}scope`]: escopo || null,
    [`${prefix}target_product_type`]: escopo === 'product_type' ? tipo || null : null,
    [`${prefix}target_product_id`]: escopo === 'product' ? produtoId || null : null,
  }
}

type AlvoDoEscopo = {
  tipo?: string | null
  produtoNome?: string | null
}

/**
 * Rótulo legível do escopo e alvo, para mostrar na tela.
 *
 * - `all` no bloco: "Todas as VMs".
 * - `all` no desconto: "Todos os produtos".
 * - `product_type`: "Tipo: <rótulo do tipo>" ou "Tipo: não definido" se ausente.
 * - `product`: "Produto: <nome>" ou "Produto: não definido" se ausente.
 * - outro escopo ou desconhecido: "Não definido".
 *
 * @param modalidade A modalidade do contrato.
 * @param escopo O escopo.
 * @param alvo Objeto com `tipo` (chave do tipo) e/ou `produtoNome` (nome do produto).
 */
export function rotuloDoEscopo(
  modalidade: ModalidadeDoContrato,
  escopo: EscopoDoContrato | null | undefined,
  alvo?: AlvoDoEscopo,
): string {
  if (escopo === 'all') {
    return modalidade === 'resource_block' ? 'Todas as VMs' : 'Todos os produtos'
  }

  if (escopo === 'product_type') {
    const tipo = alvo?.tipo
    if (!tipo) return 'Tipo: não definido'
    const tipoInfo = TIPOS_DE_PRODUTO_DO_ESCOPO.find((t) => t.value === tipo)
    return tipoInfo ? `Tipo: ${tipoInfo.label}` : 'Tipo: não definido'
  }

  if (escopo === 'product') {
    const nome = alvo?.produtoNome
    return nome ? `Produto: ${nome}` : 'Produto: não definido'
  }

  return 'Não definido'
}
